import * as braze from '@braze/web-sdk';
import type { InAppMessage, InAppMessageButton } from '@braze/web-sdk';
import { Store } from 'redux';

import { selectIsUserLoggedIn } from '@bolteu/redux-session';

import config from '../config/index';
import { AccountService } from './AccountService';
import { MixpanelService, type EventProps } from './MixpanelService';
import { TargetingService } from './TargetingService';

export enum BrazeCustomEvent {
  MERCHANT_PORTAL_NAVIGATION = 'merchant_portal_navigation',
}

export enum InAppMessageMixpanelEvent {
  VIEWED = 'In-app Viewed',
  CLICKED = 'In-app Clicked',
  DISMISSED = 'In-app Dismissed',
}

function getMessageType(message: InAppMessage): string {
  if ('slideFrom' in message) {
    return 'slide_up';
  }
  if ('orientation' in message) {
    return 'full_screen';
  }
  if ('messageFields' in message) {
    return 'html';
  }
  if ('buttons' in message) {
    return 'modal';
  }
  return 'unknown';
}

function getMessageUri(message: InAppMessage): string | null {
  return 'uri' in message && typeof message.uri === 'string' ? message.uri : null;
}

function getMessageButtons(message: InAppMessage): InAppMessageButton[] {
  return 'buttons' in message && Array.isArray(message.buttons) ? message.buttons : [];
}

export class BrazeService {
  private inAppMessageSubscriptionId: string | undefined;

  constructor(
    private globalReduxStore: Store,
    private targetingService: TargetingService,
    private accountService: AccountService,
    private mixpanelService: MixpanelService,
  ) {
    const { apiKey, apiUrl } = config.braze;
    if (apiKey && apiUrl) {
      this.init(apiKey, apiUrl);
    }
  }

  private init(apiKey: string, apiUrl: string) {
    const isInited = braze.initialize(apiKey, {
      baseUrl: apiUrl,
    });

    if (isInited) {
      if (this.isLoggedIn()) {
        this.subscribeToInAppMessages();
      }

      braze.openSession();

      // this sub is never removed, but so long as we expect a single BrazeService instance,
      // the footprint will not be significant.
      this.globalReduxStore.subscribe(() => {
        if (this.isLoggedIn()) {
          this.subscribeToInAppMessages();
        } else {
          this.unsubscribeFromInAppMessages();
        }
      });
    }
  }

  private isBrazeWebSdkEnabled(): boolean {
    return Boolean(
      this.targetingService.getParameters().delivery_provider_portal_flags?.enable_braze_web_sdk,
    );
  }

  private isLoggedIn(): boolean {
    return selectIsUserLoggedIn(this.globalReduxStore.getState());
  }

  private subscribeToInAppMessages() {
    if (this.inAppMessageSubscriptionId) {
      return;
    }

    this.inAppMessageSubscriptionId = braze.subscribeToInAppMessage((message) => {
      if (!this.isLoggedIn()) {
        return;
      }

      // A control message renders nothing and exposes no click/dismiss events; showing it
      // only logs the multivariate control enrolment to Braze.
      if (message.isControl) {
        braze.showInAppMessage(message);
        return;
      }

      // Braze only replays events to subscribers registered before the message is shown.
      this.subscribeToInAppMessageEvents(message);
      braze.showInAppMessage(message, null, () => {
        this.trackInAppMessageEvent(InAppMessageMixpanelEvent.VIEWED, message);
      });
    });
  }

  private subscribeToInAppMessageEvents(message: InAppMessage) {
    message.subscribeToClickedEvent(() => {
      this.trackInAppMessageEvent(InAppMessageMixpanelEvent.CLICKED, message, {
        uri: getMessageUri(message),
      });
    });

    // Braze suppresses the dismissed event when a message is closed by a click, so a single
    // message yields either a click or a dismissal, never both.
    message.subscribeToDismissedEvent(() => {
      this.trackInAppMessageEvent(InAppMessageMixpanelEvent.DISMISSED, message, {
        dismissType: message.dismissType,
      });
    });

    // Button clicks bypass the message-level clicked event and have their own subscription.
    getMessageButtons(message).forEach((button) => {
      button.subscribeToClickedEvent(() => {
        this.trackInAppMessageEvent(InAppMessageMixpanelEvent.CLICKED, message, {
          uri: button.uri ?? null,
          buttonId: button.id ?? null,
          buttonText: button.text,
        });
      });
    });
  }

  private trackInAppMessageEvent(
    event: InAppMessageMixpanelEvent,
    message: InAppMessage,
    props?: EventProps,
  ) {
    const selectedProvider = this.accountService.getSelectedProviderData$().state;
    if (!selectedProvider) {
      return;
    }

    this.mixpanelService.trackProviderEvent(event, {
      providerId: selectedProvider.providerId,
      cityId: selectedProvider.cityId,
      brazeMessageId: message.triggerId ?? null,
      messageType: getMessageType(message),
      ...props,
    });
  }

  private unsubscribeFromInAppMessages() {
    if (!this.inAppMessageSubscriptionId) {
      return;
    }

    braze.removeSubscription(this.inAppMessageSubscriptionId);
    this.inAppMessageSubscriptionId = undefined;
  }

  changeUser(providerId: number) {
    if (braze.isInitialized()) {
      braze.changeUser(`delivery-provider-${providerId}`);
    }
  }

  setLanguage(language: string) {
    if (braze.isInitialized()) {
      braze
        .getUser()
        ?.setCustomUserAttribute('portal_language', language || window.navigator.language);
    }
  }

  setHasSponsoredListingsAccess(hasSponsoredListingsAccess: boolean) {
    if (braze.isInitialized()) {
      braze
        .getUser()
        ?.setCustomUserAttribute('has_sponsored_listings_access', hasSponsoredListingsAccess);
    }
  }

  logCustomEvent(eventName: BrazeCustomEvent, eventProperties?: Record<string, unknown>) {
    if (braze.isInitialized() && this.isBrazeWebSdkEnabled()) {
      braze.logCustomEvent(eventName, eventProperties);
    }
  }
}
